import { DateTime } from 'luxon';
import { TIME_UNITS } from './constants';

interface TimeUnit {
  limit: number
  singular: string
  plural: string
}

export const DEFAULT_TIME_ZONE = 'America/Los_Angeles';
export const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy';
export const SHORT_MONTH_DAY_FORMAT = 'LLL d';
export const SHORT_MONTH_DAY_YEAR_FORMAT = 'LLL d, yyyy';
export enum DATE_FIELD {
  Day = 'day',
  Month = 'month',
  Year = 'year',
  Quarter = 'quarter',
}

const DATE_NOT_SET = 'N/A';
const TIME_UNIT_DEFINITIONS: TimeUnit[] = [
  { limit: 365 * 24 * 60 * 60, singular: 'year', plural: 'years' },
  { limit: 30 * 24 * 60 * 60, singular: 'month', plural: 'months' },
  { limit: 24 * 60 * 60, singular: 'day', plural: 'days' },
  { limit: 60 * 60, singular: 'hour', plural: 'hours' },
  { limit: 60, singular: 'minute', plural: 'minutes' },
];

const calculateSecondsElapsed = (date: Date): number => {
  return Math.floor((Date.now() - date.getTime()) / 1000);
};

const formatSecondsAgo = (seconds: number): string => {
  const label = seconds === 1 ? TIME_UNITS.SECOND : TIME_UNITS.SECONDS;
  return `${seconds} ${label} ago`;
};

const formatTimeUnit = (seconds: number, unit: TimeUnit): string => {
  const count = Math.floor(seconds / unit.limit);
  const label = count === 1 ? `a ${unit.singular}` : `${count} ${unit.plural}`;

  return `${label} ago`;
};

export const parseDateTime = (input: string): DateTime => {
  let parsedDateTime = DateTime.fromISO(input);

  if (!parsedDateTime.isValid) {
    parsedDateTime = DateTime.fromRFC2822(input);
  }

  if (!parsedDateTime.isValid) {
    const jsDate = new Date(input);
    parsedDateTime = DateTime.fromJSDate(jsDate);
  }

  if (!parsedDateTime.isValid) {
    throw new Error(`Invalid date provided: "${input}"`);
  }

  return parsedDateTime;
};

export const formatFriendlyDate = (input: string): string => {
  const parsedDateTime = parseDateTime(input);

  const secondsAgo = calculateSecondsElapsed(parsedDateTime.toJSDate());

  if (secondsAgo < 0) {
    return TIME_UNITS.TODAY;
  }

  if (secondsAgo < 60) {
    return formatSecondsAgo(secondsAgo);
  }

  for (const unit of TIME_UNIT_DEFINITIONS) {
    if (secondsAgo >= unit.limit) {
      return formatTimeUnit(secondsAgo, unit);
    }
  }

  return TIME_UNITS.TODAY;
};

export const getFormattedDate = (dateTime?: string, format?: string | null, timeZone?: string | null): string => {
  if (!dateTime || !DateTime.fromISO(dateTime).isValid) {
    return DATE_NOT_SET;
  }

  return DateTime.fromISO(dateTime, { zone: timeZone ?? DEFAULT_TIME_ZONE }).toFormat(format ?? DEFAULT_DATE_FORMAT);
};

export const getDateTimeField = (dateTime: string, field: DATE_FIELD, timeZone?: string | null): number => {
  return DateTime.fromISO(dateTime, { zone: timeZone ?? DEFAULT_TIME_ZONE })[field];
};
